import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useFetch from '../../../../services/useFetch.js';
import { tokens } from '../../../../theme';

const emptyRow = () => ({
  name: '',
  id: null,
  type: 'regular',
  units: [{ amount: '', measurement: '' }],
});

/**
 * Recipe ingredient rows — name / amount / UoM.
 * Supports onDataChange (preferred) or legacy updateIngredients.
 */
export default function AddIngredient({ onDataChange, updateIngredients }) {
  const notify = onDataChange || updateIngredients;
  const { data: ingredients = [], load } = useFetch('api/ingredients', false);
  const [rows, setRows] = useState([emptyRow()]);

  const options = useMemo(
    () => (Array.isArray(ingredients) ? ingredients : []),
    [ingredients],
  );

  const push = (next) => {
    setRows(next);
    notify?.(next);
  };

  useEffect(() => {
    notify?.(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial sync only
  }, []);

  const setIngredient = (index, selected) => {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      if (!selected) {
        return { ...row, name: '', id: null, units: [{ amount: row.units[0]?.amount ?? '', measurement: '' }] };
      }
      return {
        ...row,
        name: selected.ingredient,
        id: selected.ingredientId,
        units: [{
          amount: row.units[0]?.amount ?? '',
          measurement: selected.recipeUoM || '',
        }],
      };
    });
    push(next);
  };

  const setAmount = (index, amount) => {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      return {
        ...row,
        units: [{
          ...row.units[0],
          amount: amount === '' ? '' : Number(amount),
        }],
      };
    });
    push(next);
  };

  const addRow = () => push([...rows, emptyRow()]);

  const removeRow = (index) => {
    if (rows.length === 1) {
      push([emptyRow()]);
      return;
    }
    push(rows.filter((_, i) => i !== index));
  };

  const selectedOption = (row) => (
    options.find((ing) => ing.ingredientId === row.id)
    || (row.name ? { ingredient: row.name, ingredientId: row.id, recipeUoM: row.units[0]?.measurement } : null)
  );

  return (
    <Stack spacing={1.25}>
      {rows.map((row, index) => (
        <Stack
          key={`ing-row-${index}`}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          sx={{
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${tokens.border}`,
            backgroundColor: tokens.bg,
          }}
        >
          <Autocomplete
            fullWidth
            size="small"
            loading={load}
            options={options}
            value={selectedOption(row)}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.ingredient || '')}
            isOptionEqualToValue={(a, b) => a?.ingredientId === b?.ingredientId}
            onChange={(_, value) => setIngredient(index, value)}
            renderInput={(params) => (
              <TextField {...params} label="Ingredient" placeholder="Select ingredient" />
            )}
            sx={{ flex: 2, minWidth: 0 }}
          />
          <TextField
            size="small"
            type="number"
            label="Amount"
            inputProps={{ min: 0, step: 'any' }}
            value={row.units[0]?.amount ?? ''}
            onChange={(e) => setAmount(index, e.target.value)}
            sx={{ width: { xs: '100%', sm: 110 }, flexShrink: 0 }}
          />
          <TextField
            size="small"
            label="UoM"
            value={row.units[0]?.measurement || '—'}
            disabled
            sx={{ width: { xs: '100%', sm: 100 }, flexShrink: 0 }}
          />
          <IconButton
            aria-label="Remove ingredient"
            onClick={() => removeRow(index)}
            sx={{ color: tokens.danger, alignSelf: { xs: 'flex-end', sm: 'center' } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={addRow}
          sx={{ color: tokens.accent }}
        >
          Add ingredient
        </Button>
        {!rows.some((r) => r.id != null) && (
          <Typography variant="caption" sx={{ display: 'block', color: tokens.inkMuted, mt: 0.5 }}>
            Pick raw ingredients and amounts used in this recipe.
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
